import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import {
  combineLatestWith,
  distinctUntilChanged,
  filter,
  map,
  takeUntil,
  tap,
} from 'rxjs/operators';
import { LagDeviceModel } from 'src/app/@store/cascades/lag-device.model';

import {
  ActivateDeactivateCommands,
  BundleUpdateProgressEnum,
  CirculationPumpStatusProperties,
  DeviceRoles,
  Feature,
  FeatureCommands,
  FeatureNames,
  FeaturePropertyKey,
  IsActiveProperty,
  IsEnabledProperty,
  OperatingProgramCommands,
  ServiceTimeBasedCommands,
  ServiceTimeBasedProperties,
  SignalProperties,
  StatusType,
  SummaryConsumptionProperties,
} from '@vi-iot/client';

import { ViUIService } from '@vi-iot/ui';

import {
  GTMService,
  RemoteDiagnosticsBackgroundService,
  RoutingService,
  UIService,
} from '@services';
import {
  ActiveErrorData,
  ActiveErrorsStore,
  AuthStore,
  BundleUpdateProcessType,
  CascadeLagDeviceDetails,
  DashboardTypes,
  DevicesStore,
  DocumentsStore,
  EnergyHistoricalDataStore,
  EventsStore,
  FeatureService,
  FirmwareUpdateStore,
  GatewayStatus,
  GatewayStore,
  HemsStore,
  Indicator,
  InstallationDetailsStore,
  InstallationFeaturesStore,
  InstallationsStore,
  NoteStore,
  RemoteDiagnosticsStore,
  RichDevice,
  StatusPageStore,
  isReady,
  localStorageTargetBundleVersionKey,
} from '@store';

import { ViewportRuler } from '@angular/cdk/overlay';
import { wifiSignalThreshold } from '@common';
import { environment } from 'src/environments/environment';
import { Chart } from '../../features/chart-bar/models/chart.model';
import { LiveUpdatesDataService } from '../../shared/live-updates/live-updates-data.service';
import { PaidFeaturesEnum } from '../../shared/pipes/paid-feature-available/paid-feature-available.pipe';
import { InstallationService } from '../services/installation.service';

import { SocketIoService } from 'src/app/shared/socket-io/socket-io.service';
import { ReadyPipe } from '../../operational-data-common/feature-pipes/ready.pipe';
import { CLOSE_MODAL_ACTIONS } from '../../shared/common/enums';
import { ViguideBreakpoints } from '../../shared/common/ui-constants';
import { PillType } from '../../shared/components/pill/pill.model';
import { DragDropLayoutService } from './drag-drop-layout/drag-drop-layout.service';
import {
  AvailableSidePanels,
  DiagnosticsOverlayTypeEnum,
  DiagnosticsUrlTypeEnum,
  FeaturesDetailsOverlayTypeEnum,
  PartnerAccessOverlayTypeEnum,
  RemoteServiceOverlayTypeEnum,
  RemoteServiceUrlTypeEnum,
  SingleRoomControlOverlayTypeEnum,
  SingleRoomControlUrlTypeEnum,
} from './installation-overlay.model';
import { RefrigerantCircuitHistoryModalComponent } from './refrigerant-circuit-features-panel/refrigerant-circuit-history-modal/refrigerant-circuit-history-modal.component';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'app-installation',
  templateUrl: './installation.component.html',
  styleUrls: ['./installation.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class InstallationComponent implements OnInit, OnDestroy {
  readonly featurePropertyKey = FeaturePropertyKey;
  readonly pillType = PillType;
  readonly buyRepeaterLink = environment.buyRepeaterLink;
  readonly PaidFeatures = PaidFeaturesEnum;
  readonly wifiSignalThreshold = wifiSignalThreshold;
  readonly StatusPageStatus = StatusType;

  FeatureNames = FeatureNames;
  DashboardTypes = DashboardTypes;
  refrigerantCircuitHistoryDialogRef: MatDialogRef<RefrigerantCircuitHistoryModalComponent>;

  isOverlayOpen = false;
  isDiagnosticOverlayOpen = false;
  isRemoteServiceOverlayOpen = false;
  isSingleRoomControlOverlayOpen = false;
  isPartnerAccessSettingsOverlayOpen = false;
  availableSidePanels = AvailableSidePanels;
  availableOverlays = FeaturesDetailsOverlayTypeEnum;
  isSidenavPanel: Boolean = false;
  isLegacyDevice: boolean;
  isBreadcrumbSearch: boolean;
  installationId: number;
  gatewaySerial: string;
  ngUnsubscribe: Subject<void> = new Subject<void>();
  firstCircuitId$: BehaviorSubject<string> = new BehaviorSubject<string>(null);
  deviceIndicator$: Observable<Indicator>;
  isMultiFamily: boolean;
  isMaintenanceNeeded: boolean;
  selectedRefrigerantCircuitId: string;
  boilerSerial: string;
  plantOverviewCountries = ['DE'];
  lowWifiSignalStrength$: Observable<boolean>;
  isRemoteDiagnsticsSessionOpen: boolean;
  isMainDeviceB2C$: Observable<boolean>;
  activeGatewayErrors$: Observable<ActiveErrorData[]>;
  gatewayStatus$: Observable<GatewayStatus>;
  toolTipInfo$: Observable<string>;
  device: RichDevice;

  // Circuits
  circuitPumpStatus$: Observable<Feature<CirculationPumpStatusProperties, never>>;
  heatingPump$: Observable<Feature<IsActiveProperty, never>>;
  heatingZoneMode$: Observable<Feature<IsActiveProperty, OperatingProgramCommands>>;

  // Drag and Drop
  dragDropEditMode = false;
  refreshGridAfterDragAndDropAction = true;

  // System
  emergencyMode$: Observable<Feature<IsActiveProperty, ActivateDeactivateCommands>>;

  // Cascades
  isCascadeInstallation: boolean;
  lagDevices$: Observable<LagDeviceModel[]>;
  lagDeviceOverlayIdx: string;
  lagDevicesImageUri: string;

  // Summary consumption
  gasConsumptionSummaryHeating$: Observable<Feature<SummaryConsumptionProperties, never>>;
  gasConsumptionSummaryDhw$: Observable<Feature<SummaryConsumptionProperties, never>>;
  powerConsumptionSummaryHeating$: Observable<Feature<SummaryConsumptionProperties, never>>;
  powerConsumptionSummaryDhw$: Observable<Feature<SummaryConsumptionProperties, never>>;
  autarkySummary$: Observable<Feature<SummaryConsumptionProperties, never>>;

  // Charts
  powerProductionChartData$: Observable<Chart>;
  gasConsumptionChartData$: Observable<Chart>;
  powerConsumptionChartData$: Observable<Chart>;
  solarPowerProductionChartData$: Observable<Chart>;
  heatProductionChartData$: Observable<Chart>;

  constructor(
    public hemsStore: HemsStore,
    public installationDetailsStore: InstallationDetailsStore,
    public noteStore: NoteStore,
    public activeErrorsStore: ActiveErrorsStore,
    public featureService: FeatureService,
    public installationService: InstallationService,
    public gatewayStore: GatewayStore,
    public devicesStore: DevicesStore,
    public uiService: UIService,
    public authStore: AuthStore,
    public remoteDiagnosticsStore: RemoteDiagnosticsStore,
    public installationsStore: InstallationsStore,
    public statusPageStore: StatusPageStore,
    private activatedRoute: ActivatedRoute,
    private router: Router,
    private gtmService: GTMService,
    private viUIService: ViUIService,
    private matDialog: MatDialog,
    private dragAndDropLayoutService: DragDropLayoutService,
    private cdr: ChangeDetectorRef,
    private remoteDiagnosticsBackgroundService: RemoteDiagnosticsBackgroundService,
    private liveUpdatesDataService: LiveUpdatesDataService,
    private firmwareUpdateStore: FirmwareUpdateStore,
    private readyPipe: ReadyPipe,
    private viewportRuler: ViewportRuler,
    private energyHistoricalDataStore: EnergyHistoricalDataStore,
    private routingService: RoutingService,
    private installationFeaturesStore: InstallationFeaturesStore,
    private socketService: SocketIoService,
    private translateService: TranslateService,
    private eventsStore: EventsStore,
    private documentStore: DocumentsStore
  ) {
    const navigation = this.router.getCurrentNavigation();
    this.isBreadcrumbSearch = navigation?.extras?.state?.searchBreadcrumb;
    this.deviceIndicator$ = this.featureService.selectDeviceIndicators();
  }

  ngOnInit(): void {
    this.toggleDragAndDropFunctionality(); // manage on screen loading
    this.reactOnScreenChangesToManageDragAndDrop(); // listen to width changes

    this.activatedRoute.queryParams.subscribe((queryParams) => {
      this.gatewaySerial = queryParams?.gatewaySerial;
      if (this.gatewaySerial) {
        this.isCascadeInstallation = false;
        this.installationDetailsStore.getPanelsOrder(this.gatewaySerial);
        this.subscribeToOTAOnGoing();
      }
    });

    this.remoteDiagnosticsStore
      .isActiveSessionAvailableBasedOnStatusFeatureOnly()
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((isActiveSessionAvailable) => {
        this.isRemoteDiagnsticsSessionOpen = isActiveSessionAvailable;
        this.cdr.detectChanges();
      });

    this.installationFeaturesStore
      .selectInstallationDeviceFeature(FeatureNames.circuits)
      .pipe(
        takeUntil(this.ngUnsubscribe),
        filter((feature) => isReady(feature)),
        filter(
          (feature: Feature<IsEnabledProperty, never>) =>
            !!feature?.properties?.enabled?.value?.length
        ),
        map((feature) => feature.properties.enabled.value[0])
      )
      .subscribe((circuitId) => {
        this.installationService.selectedCircuitId$.next(circuitId);
        this.installationService.firstCircuitId$.next(circuitId);
        this.firstCircuitId$.next(circuitId);
      });

    this.devicesStore.isMultifamily$.pipe(takeUntil(this.ngUnsubscribe)).subscribe((isMulti) => {
      this.isMultiFamily = isMulti;
    });

    this.lowWifiSignalStrength$ = this.installationFeaturesStore
      .selectInstallationGatewayFeature<SignalProperties, never>(
        FeatureNames.tcuWifi,
        this.gatewaySerial
      )
      .pipe(
        filter((feature) => !!this.readyPipe.transform(feature)),
        map((feature) => feature?.properties?.strength?.value <= this.wifiSignalThreshold)
      );

    // System
    this.emergencyMode$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.heatingDeviceEmergencyOperation
    );

    // Maintenance
    this.installationFeaturesStore
      .selectInstallationDeviceFeature<ServiceTimeBasedProperties, ServiceTimeBasedCommands>(
        FeatureNames.serviceTimeBased
      )
      .pipe(
        takeUntil(this.ngUnsubscribe),
        filter((feature: Feature<ServiceTimeBasedProperties, ServiceTimeBasedCommands>) =>
          isReady(feature)
        ),
        map((feature) => {
          const featuresValue: boolean | number =
            feature?.properties?.serviceDue?.value &&
            feature?.properties?.serviceIntervalMonths?.value &&
            feature?.properties?.activeMonthSinceLastService?.value;

          return !!featuresValue;
        })
      )
      .subscribe((isMaintenanceNeeded: boolean) => {
        this.isMaintenanceNeeded = isMaintenanceNeeded;
      });

    this.installationService.selectedRefrigerantCircuitId$
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((id) => {
        this.selectedRefrigerantCircuitId = id;
      });

    this.installationDetailsStore.device$
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((device) => {
        this.lagDevicesImageUri = device.image;
        this.boilerSerial = device.boilerSerial;
        this.initActiveGatewayErrors();
      });

    this.activatedRoute.params.subscribe((param) => {
      this.installationId = +param['id'];
      this.getData();
    });

    this.documentStore.getXotaAgreementStatus(environment.xOtaId, this.installationId);

    this.initOverlay();
    this.installationService.subscribeToDefrostingFeature();

    this.remoteDiagnosticsBackgroundService.subscribeToRemoteDiagnosticsStatusFeature(
      this.gatewaySerial
    );

    // Cascade Lag Devices
    this.lagDevices$ = this.installationDetailsStore.getSequencedCascadeLagDevices$.pipe(
      takeUntil(this.ngUnsubscribe),
      map((lagDeviceDetails: CascadeLagDeviceDetails[]) =>
        lagDeviceDetails?.map((lagDevice) => ({
          ...lagDevice,
          imageUri: this.lagDevicesImageUri,
        }))
      ),
      tap((devices: LagDeviceModel[]) => {
        if (!!devices?.length) {
          if (!this.isCascadeInstallation) {
            this.isCascadeInstallation = true;
          }
        }
      })
    );

    this.checkUrlAndOpenOverlayIfNeeded();

    // Summary consumption
    this.gasConsumptionSummaryHeating$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.gasConsumptionSummaryHeating
    );
    this.gasConsumptionSummaryDhw$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.gasConsumptionSummaryDhw
    );
    this.powerConsumptionSummaryHeating$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.powerConsumptionSummaryHeating
    );
    this.powerConsumptionSummaryDhw$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.powerConsumptionSummaryDhw
    );
    this.autarkySummary$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.heatingPowerProductionDemandCoverageRaw
    );

    // Charts

    this.heatProductionChartData$ = this.featureService.selectHeatProductionChartData();
    this.powerConsumptionChartData$ = this.featureService.selectPowerConsumptionChartData();
    this.powerProductionChartData$ = this.featureService.selectPowerProductionChartData();
    this.gasConsumptionChartData$ = this.featureService.selectGasConsumptionChartData();
    this.solarPowerProductionChartData$ = this.featureService.selectSolarPowerProductionChartData();

    this.setPumpStatus();
    this.setHeatingZoneMode();

    this.gatewayStore
      .shouldDisplayWarningHeatingCircuitModal()
      .pipe(
        filter(
          (shouldDisplayHCWarningModal) =>
            shouldDisplayHCWarningModal && !this.isSingleRoomControlOverlayOpen
        ),
        takeUntil(this.ngUnsubscribe)
      )
      .subscribe(() => {
        this.subscribeToHCWarningModal();
      });

    // Recommendations
    // this.fetchRecommendationsIfPossible();

    this.installationDetailsStore.triggerMonetizationFeatures(this.installationId);

    this.devicesStore.selectedDevice$
      .pipe(
        filter((device) => !!device),
        distinctUntilChanged(
          (prev: RichDevice, curr: RichDevice) => prev.selectId === curr.selectId
        ),
        tap((device) => {
          // TODO: This is triggered too many times, each device selection change
          this.device = device;

          const isLegacy = device.roles.find((role) => {
            return role === DeviceRoles.Legacy;
          });
          this.isLegacyDevice = !!isLegacy;
          this.energyHistoricalDataStore.resetStore();
        }),
        combineLatestWith(
          this.installationDetailsStore.shouldFetchEnergyConsumptionDataForLegacyHeatpumps$,
          this.devicesStore.isEcotronicDevice$
        ),
        filter(([_device, shouldFetch]) => shouldFetch),
        takeUntil(this.ngUnsubscribe)
      )
      .subscribe(([device, _shouldFetch, isEcotronicDevice]) => {
        this.energyHistoricalDataStore.getLegacyHPChartData(
          device.gatewaySerial,
          true,
          isEcotronicDevice
            ? FeatureNames.legacyBoilerFuelConsumption
            : FeatureNames.legacyHeatPumpConsumption
        );
      });

    this.uiService.activeOverlay$
      .pipe(
        combineLatestWith(
          this.installationDetailsStore.shouldFetchEnergyConsumptionDataForLegacyHeatpumps$,
          this.devicesStore.isEcotronicDevice$
        ),
        filter(([_overlay, shouldFetch]) => shouldFetch),
        takeUntil(this.ngUnsubscribe)
      )
      .subscribe(([overlay, _shouldFetch, isEcotronicDevice]) => {
        if (
          overlay === FeaturesDetailsOverlayTypeEnum.ElectricityConsumption ||
          overlay === FeaturesDetailsOverlayTypeEnum.FuelConsumption
        ) {
          this.energyHistoricalDataStore.getLegacyHPChartData(
            this.gatewaySerial,
            false,
            isEcotronicDevice
              ? FeatureNames.legacyBoilerFuelConsumption
              : FeatureNames.legacyHeatPumpConsumption
          );
        }
      });

    this.isMainDeviceB2C$ = this.installationDetailsStore.isMainDeviceB2C$.pipe(
      takeUntil(this.ngUnsubscribe)
    );
    this.gatewayStatus$ = this.installationDetailsStore.getGatewayStatus();

    this.toolTipInfo$ = this.uiService.activeOverlay$.pipe(
      map((activeOverlay: FeaturesDetailsOverlayTypeEnum) => {
        if (
          activeOverlay === this.availableOverlays.GasConsumption ||
          activeOverlay === this.availableOverlays.ElectricityConsumption ||
          activeOverlay === this.availableOverlays.HeatProduction ||
          activeOverlay === this.availableOverlays.PowerProduction ||
          activeOverlay === this.availableOverlays.FuelConsumption
        ) {
          return this.translateService.instant(
            '+INSTALLATIONS.INSTALLATION.OVERLAY.CONSUMPTION_TOOLTIP'
          );
        } else {
          return null;
        }
      })
    );
  }

  subscribeToOTAOnGoing(): void {
    this.installationDetailsStore
      .isOTAOnGoing(this.gatewaySerial)
      .pipe(
        filter((isOTAOnGoing) => isOTAOnGoing),
        takeUntil(this.ngUnsubscribe)
      )
      .subscribe(() => {
        const localStorageKey = `${localStorageTargetBundleVersionKey}-${this.gatewaySerial}`;

        const firmwareUpdateLocalState = JSON.parse(localStorage.getItem(localStorageKey));

        this.firmwareUpdateStore.openFirmwareUpdateModal(this.gatewaySerial);

        if (
          firmwareUpdateLocalState?.progress === BundleUpdateProgressEnum.Ongoing ||
          firmwareUpdateLocalState?.progress === BundleUpdateProgressEnum.Scheduled
        ) {
          if (
            firmwareUpdateLocalState?.processType ===
            BundleUpdateProcessType.CHECK_FOR_BUNDLE_UPDATE
          ) {
            this.firmwareUpdateStore.checkOverallProgress();
          } else {
            this.firmwareUpdateStore.checkOverallProgressFirmwareUpdate();
          }
        }
      });
  }

  subscribeToHCWarningModal(): void {
    this.installationService.openMissedHCAssignmentDialog().subscribe((action) => {
      if (action === CLOSE_MODAL_ACTIONS.OPEN_SRC_OVERLAY) {
        this.uiService.openSingleRoomControlOverlay(
          SingleRoomControlOverlayTypeEnum.SingleRoomControlCommissioning,
          this.installationId,
          this.gatewaySerial
        );
        this.cdr.detectChanges();
      }
    });
  }

  ngOnDestroy(): void {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
    this.installationService.resetSubscriptions();
    this.devicesStore.resetSelectedDeviceId();
    this.viUIService.unhideBodyScrollbar();
    this.uiService.closeActiveOverlay();
    this.dragAndDropLayoutService.renderedPanels = {};
    this.routingService.installationIdWithModifiedHistory$.next(null);
    this.installationFeaturesStore.removeAllFeatures();
    this.liveUpdatesDataService.unsubscribeAll();
    sessionStorage.removeItem(this.socketService.liveUpdatesSubscriptionSessionKey);
    this.eventsStore.clearEventLogDownloadData();
  }

  private getData(): void {
    const id = this.installationId;
    this.devicesStore.areDevicesLoadedAndOneIsSelected$
      .pipe(filter(Boolean), takeUntil(this.ngUnsubscribe))
      .subscribe(() => {
        this.activeErrorsStore.loadActiveErrors(id);
      });
    this.authStore
      .getCompanyName()
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((companyName) => {
        if (!!companyName) {
          this.noteStore.getNote(id + '');
        }
      });
  }

  private initActiveGatewayErrors(): void {
    this.activeGatewayErrors$ = this.activeErrorsStore
      .getActiveGatewayErrors(this.gatewaySerial, this.boilerSerial)
      .pipe(takeUntil(this.ngUnsubscribe));
  }

  toggleDragDropEditPanel(): void {
    this.dragDropEditMode = !this.dragDropEditMode;
  }

  openRefrigerantCircuitHistoryDialog(): void {
    this.refrigerantCircuitHistoryDialogRef = this.matDialog.open(
      RefrigerantCircuitHistoryModalComponent,
      {
        width: '800px',
        panelClass: 'refrigerant-circuit-history-dialog',
        data: {
          boilerSerial: this.boilerSerial,
        },
        disableClose: true,
        autoFocus: false,
      }
    );
  }

  activateEmergencyMode(): void {
    this.installationFeaturesStore.updateFeature(
      FeatureNames.heatingDeviceEmergencyOperation,
      true,
      'active',
      '',
      FeatureCommands.activate
    );
    this.gtmService.track('onConfirm', 'Emergency mode', 'systemDetails');
  }

  cancelEmergencyModeConfirmation(): void {
    this.gtmService.track('onCancel', 'Emergency mode confirmation', 'systemDetails');
  }

  // TODO VIG-3076 commented temporarily
  //
  // onDefrostingProcessChange(value: boolean): void {
  //   this.installationService.onDefrostingProcessChange(value);
  //   this.gtmService.track('onConfirm', 'Defrosting process change', 'systemDetails');
  // }

  //
  // cancelDefrostingConfirmation(): void {
  //   this.gtmService.track('onCancel', 'Defrosting process change confirmation', 'systemDetails');
  // }

  openDiagnosticOverlay(): void {
    this.uiService.openDiagnosticOverlay(
      DiagnosticsOverlayTypeEnum.ExtendedParameter,
      this.installationId,
      this.gatewaySerial
    );
  }

  onShowLagDeviceDetails(id: string): void {
    this.lagDeviceOverlayIdx = id;
    this.uiService.openActiveOverlay(FeaturesDetailsOverlayTypeEnum.CascadeLagDevice);
  }

  private setPumpStatus(): void {
    this.circuitPumpStatus$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.circuitCirculationPump(this.installationService.selectedCircuitId$.getValue())
    );
    this.heatingPump$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.callForHeatPump
    );
  }

  private setHeatingZoneMode(): void {
    this.heatingZoneMode$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.circuitHeatingZoneMode(this.installationService.selectedCircuitId$.getValue())
    );
  }

  private initOverlay(): void {
    this.uiService.isOverlayOpen$.pipe(takeUntil(this.ngUnsubscribe)).subscribe((isOpen) => {
      this.isOverlayOpen = isOpen;
      if (isOpen) {
        const matSidenavContentElements = document.getElementsByTagName('mat-sidenav-content');
        if (matSidenavContentElements?.length) {
          matSidenavContentElements[0].scrollTop = 0;
        }
      }
    });

    this.uiService.isDiagnosticOverlayOpen$
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((isOpen) => {
        this.isDiagnosticOverlayOpen = isOpen;
        if (isOpen) {
          const matSidenavContentElements = document.getElementsByTagName('mat-sidenav-content');
          if (matSidenavContentElements?.length) {
            matSidenavContentElements[0].scrollTop = 0;
          }
        }
      });

    this.uiService.isRemoteServiceOverlayOpen$
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((isOpen) => {
        this.isRemoteServiceOverlayOpen = isOpen;
        if (isOpen) {
          const matSidenavContentElements = document.getElementsByTagName('mat-sidenav-content');
          if (matSidenavContentElements?.length) {
            matSidenavContentElements[0].scrollTop = 0;
          }
        }
      });

    this.uiService.isSingleRoomControlOverlayOpen$
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((isOpen) => {
        this.isSingleRoomControlOverlayOpen = isOpen;
        if (isOpen) {
          const matSidenavContentElements = document.getElementsByTagName('mat-sidenav-content');
          if (matSidenavContentElements?.length) {
            matSidenavContentElements[0].scrollTop = 0;
          }
        }
      });

    this.uiService.isActivePartnerAccessSettingsOverlayOpen$
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((isOpen) => {
        this.isPartnerAccessSettingsOverlayOpen = isOpen;
        if (isOpen) {
          const matSidenavContentElements = document.getElementsByTagName('mat-sidenav-content');
          if (matSidenavContentElements?.length) {
            matSidenavContentElements[0].scrollTop = 0;
          }
        }
      });
  }

  private checkUrlAndOpenOverlayIfNeeded(): void {
    const url = this.router.url.split('?')[0];
    const childPath = url.split('/').slice(-1)[0];

    switch (childPath) {
      case DiagnosticsUrlTypeEnum.Backups: {
        this.uiService.openDiagnosticOverlay(DiagnosticsOverlayTypeEnum.DiagnosisBackupAndRestore);
        break;
      }
      case DiagnosticsUrlTypeEnum.ExtendedParameter: {
        this.uiService.openDiagnosticOverlay(DiagnosticsOverlayTypeEnum.ExtendedParameter);
        break;
      }
      case DiagnosticsUrlTypeEnum.GuidedTroubleshooting: {
        this.uiService.openDiagnosticOverlay(DiagnosticsOverlayTypeEnum.GuidedTroubleshooting);
        break;
      }
      case DiagnosticsUrlTypeEnum.HydraulicSchematic: {
        this.uiService.openDiagnosticOverlay(DiagnosticsOverlayTypeEnum.HydraulicSchematic);
        break;
      }
      case RemoteServiceUrlTypeEnum.Anomalies: {
        this.uiService.openRemoteServiceOverlay(RemoteServiceOverlayTypeEnum.Anomalies);
        break;
      }
      case RemoteServiceUrlTypeEnum.Diagnosis: {
        this.uiService.openRemoteServiceOverlay(RemoteServiceOverlayTypeEnum.Diagnosis);
        break;
      }
      case RemoteServiceUrlTypeEnum.Features: {
        this.uiService.openRemoteServiceOverlay(RemoteServiceOverlayTypeEnum.Features);
        break;
      }
      case RemoteServiceUrlTypeEnum.Report: {
        this.uiService.openRemoteServiceOverlay(RemoteServiceOverlayTypeEnum.Report);
        break;
      }
      case DiagnosticsUrlTypeEnum.Snapshots: {
        this.uiService.openDiagnosticOverlay(DiagnosticsOverlayTypeEnum.DiagnosisSnapshots);
        break;
      }
      case SingleRoomControlUrlTypeEnum.Commissioning: {
        this.uiService.openSingleRoomControlOverlay(
          SingleRoomControlOverlayTypeEnum.SingleRoomControlCommissioning
        );
        break;
      }
      case PartnerAccessOverlayTypeEnum.Settings: {
        this.uiService.openPartnerAccessOverlay(PartnerAccessOverlayTypeEnum.Settings);
        break;
      }
    }
  }

  // private fetchRecommendationsIfPossible(): void {
  //   this.viguideRecommendationsStore.shouldFetchOptimizationRecommendations$
  //     .pipe(
  //       filter((value) => !!value),
  //       take(1),
  //       takeUntil(this.ngUnsubscribe)
  //     )
  //     .subscribe(() => {
  //       this.viguideRecommendationsStore.fetchBoilerOptimizationsRecommendations();
  //     });
  // }

  private reactOnScreenChangesToManageDragAndDrop(): void {
    this.viewportRuler
      .change()
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(() => this.toggleDragAndDropFunctionality());
  }

  private toggleDragAndDropFunctionality(): void {
    if (this.viewportRuler.getViewportSize().width < ViguideBreakpoints.md) {
      this.dragAndDropLayoutService.isDragDropDisabled.next(true);
    } else {
      this.dragAndDropLayoutService.isDragDropDisabled.next(false);
    }
  }
}
